import { Box, Flex, Input, Loader, Stack, Text } from '@mantine/core'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'

import Close from '@/assets/icons/cross-circle-fill.svg'
import Exclamation from '@/assets/icons/exclamation.svg'
import Search from '@/assets/icons/search.svg'
import ResponsiveModal from '@/components/basic/ResponsiveModal'

import { useSearchStocks } from './hooks'
import styles from './index.module.css'
import StockRow from './StockRow'

interface StockListModalProps {
  visible: boolean
  onClose: () => void
}

const CONTENT_HEIGHT = 384

export default function StockListModal({ visible, onClose }: StockListModalProps) {
  const { setKeyword, keyword, stockList, isLoading } = useSearchStocks()
  const t = useTranslations('coinListModal')

  const parentRef = useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: stockList.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 64,
    overscan: 5,
  })

  useEffect(() => {
    if (visible && stockList.length > 0) {
      virtualizer.measure()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, stockList.length])

  const content = (() => {
    if (isLoading) {
      return (
        <Flex h={CONTENT_HEIGHT} align={'center'} justify={'center'}>
          <Loader />
        </Flex>
      )
    }

    if (stockList.length === 0) {
      return (
        <Flex h={CONTENT_HEIGHT} direction={'column'} align={'center'} justify={'center'}>
          <Exclamation width={80} height={80} />
          <Text mt={12} variant={'body-emphasis'}>
            {t('noResultsFound')}
          </Text>
        </Flex>
      )
    }

    return (
      <div
        ref={parentRef}
        style={{
          height: CONTENT_HEIGHT,
          overflow: 'auto',
          width: '100%',
        }}
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map(virtualItem => (
            <div
              key={virtualItem.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '64px',
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <StockRow stock={stockList[virtualItem.index]} />
            </div>
          ))}
        </div>
      </div>
    )
  })()
  return (
    <ResponsiveModal
      title={<Text variant={'heading2'}>{t('selectAnAsset')}</Text>}
      opened={visible}
      onClose={onClose}
    >
      <Box>
        <Input
          classNames={{ input: styles.searchInput }}
          placeholder={t('searchAssets')}
          leftSection={
            <Box ml={16} mr={12}>
              <Search width={20} height={20} fill={'var(--content-secondary)'} />
            </Box>
          }
          rightSectionPointerEvents="all"
          rightSection={
            keyword ? <Close className={styles.clearIcon} onClick={() => setKeyword('')} /> : null
          }
          value={keyword}
          onChange={e => setKeyword(e.currentTarget.value)}
        />

        <Stack mt={12}>{content}</Stack>
      </Box>
    </ResponsiveModal>
  )
}
